import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AutoAwesome, BrowserUpdated, DeleteForever, Refresh, Save, Stop, UploadFile, WarningAmber } from "@mui/icons-material";
import {
  AI_BOT_CONFIG_ENDPOINT,
  AI_CLEAR_KNOWLEDGE_BASE_ENDPOINT,
  AI_KNOWLEDGE_BASE_TOPICS_ENDPOINT,
  AI_REEMBED_KNOWLEDGE_BASE_ENDPOINT,
  AI_SCRAPE_WEBSITE_ENDPOINT,
  AI_STOP_SCRAPE_WEBSITE_ENDPOINT,
  AI_STOP_TRAIN_DOCUMENT_ENDPOINT,
  AI_TRAIN_DOCUMENT_ENDPOINT,
} from "../config/urls";
import { useAuth } from "../hooks/AuthProvider";
import ButtonOne from "../common/normal-button";
import CustomTabs from "../common/custom-tabs";
import AIModelPicker from "./AI/AIModelPicker";
import AIConnectionHealthBanner from "./AI/AIConnectionHealthBanner";
import AISettingsPanel from "./AI/AISettingsPanel";
import SectionCard, { FieldGroup } from "./AI/SectionCard";
import { aiFetch, errorMessage } from "./AI/aiApi";

const DOMAIN_REGEX = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

const TRAINING_STATUS = {
  idle: { label: "Not trained", color: "default" },
  running: { label: "Training…", color: "info" },
  completed: { label: "Trained", color: "success" },
  failed: { label: "Failed", color: "error" },
};

// Both values are human-readable sizes ("16 kB", "100 MB"); compare them as bytes,
// not as strings ("16 kB" > "100 MB" alphabetically).
const sizeInBytes = (value) => {
  const match = /^\s*([\d.]+)\s*(bytes|kB|KB|MB|GB|TB)?/.exec(value || "");
  if (!match) return 0;
  const units = { bytes: 1, kB: 1024, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
  return parseFloat(match[1]) * (units[match[2]] || 1);
};

const twoColumns = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 };

// An on/off setting with its explanation.
const SwitchRow = ({ label, description, checked, onChange, disabled }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 2,
      "&:not(:first-of-type)": { mt: 2 },
    }}
  >
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.82rem", color: "#888", mt: 0.25 }}>{description}</Typography>
    </Box>
    <FormControlLabel
      sx={{ m: 0 }}
      label=""
      control={
        <Switch
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          inputProps={{ "aria-label": label }}
        />
      }
    />
  </Box>
);

const StatusChip = ({ status }) => {
  const s = TRAINING_STATUS[status] || TRAINING_STATUS.idle;
  return <Chip size="small" label={s.label} color={s.color} variant={status === "idle" ? "outlined" : "filled"} />;
};

// One training source (website or document): description, status, last run and action.
const TrainingRow = ({ title, description, status, lastTrained, action, alert, children }) => (
  <Box sx={{ border: "1px solid #eceff1", borderRadius: "12px", px: 2.5, py: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{title}</Typography>
          <StatusChip status={status} />
        </Box>
        <Typography sx={{ fontSize: "0.82rem", color: "#888", mt: 0.25 }}>{description}</Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "#888" }}>
          Last trained: <strong>{lastTrained || "Never"}</strong>
        </Typography>
      </Box>
      {action}
    </Box>
    {children && <Box sx={{ mt: 2 }}>{children}</Box>}
    {alert?.message && (
      <Alert severity={alert.type || "info"} sx={{ mt: 2 }}>
        {alert.message}
      </Alert>
    )}
  </Box>
);

export default function AIBotConfiguration() {
  const { getValidToken } = useAuth();
  const canEdit = JSON.parse(localStorage.getItem("permissions") || "[]").includes("agentic_ai.edit");

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("models");
  const [snackbar, setSnackbar] = useState({ type: "", message: "" });
  const notify = (type, message) => setSnackbar({ type, message });

  // AI Bot
  const [botType, setBotType] = useState("");
  // Chat model override for the AI Bot ({source, provider, model}); null = AI models default.
  const [aiOverride, setAiOverride] = useState(null);
  const [retrievedDocumentCount, setRetrievedDocumentCount] = useState(5);
  const [rateLimit, setRateLimit] = useState(0);
  const [answerLimit, setAnswerLimit] = useState(4095);
  const [maxHistoryMessages, setMaxHistoryMessages] = useState(10);
  const [rewritePrompt, setRewritePrompt] = useState("");
  const [answerPrompt, setAnswerPrompt] = useState("");
  // Cost controls (optimize-kb-bot-costs). relevanceThreshold "" = the embedding model's default.
  const [relevanceThreshold, setRelevanceThreshold] = useState("");
  const [relevanceDefault, setRelevanceDefault] = useState(null);
  const [notFoundReply, setNotFoundReply] = useState("");
  const [knowledgeBaseTopics, setKnowledgeBaseTopics] = useState("");
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [historyAnswerChars, setHistoryAnswerChars] = useState(300);
  const [maxAnswerTokens, setMaxAnswerTokens] = useState(250);
  const [answerThinking, setAnswerThinking] = useState("none");
  const [conciseAnswers, setConciseAnswers] = useState(true);
  const [broadQuestionSearch, setBroadQuestionSearch] = useState(false);
  const [condenseLongAnswers, setCondenseLongAnswers] = useState(false);
  const [skipFirstRewrite, setSkipFirstRewrite] = useState(true);
  const [smallTalkEnabled, setSmallTalkEnabled] = useState(true);
  const [greetingReply, setGreetingReply] = useState("");
  const [thanksReply, setThanksReply] = useState("");
  const [closingReply, setClosingReply] = useState("");
  const [botErrors, setBotErrors] = useState({});
  const [savingBot, setSavingBot] = useState(false);
  const [healthRefreshKey, setHealthRefreshKey] = useState(0);

  // Knowledge base
  const [knowledgeBaseSize, setKnowledgeBaseSize] = useState("");
  const [knowledgeBaseLimit, setKnowledgeBaseLimit] = useState("");
  const [knowledgeBaseStale, setKnowledgeBaseStale] = useState(false);
  const [knowledgeBaseChunks, setKnowledgeBaseChunks] = useState(0);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [baseDomain, setBaseDomain] = useState("");
  const [chunkSize, setChunkSize] = useState(300);
  const [chunkOverlap, setChunkOverlap] = useState(50);
  const [kbErrors, setKbErrors] = useState({});
  const [scrapeStatus, setScrapeStatus] = useState("idle");
  const [timeSinceLastScrape, setTimeSinceLastScrape] = useState("");
  const [scrapeAlert, setScrapeAlert] = useState({ type: "", message: "" });
  const [documentTrainingStatus, setDocumentTrainingStatus] = useState("idle");
  const [timeSinceLastDocumentTraining, setTimeSinceLastDocumentTraining] = useState("");
  const [documentTrainingAlert, setDocumentTrainingAlert] = useState({ type: "", message: "" });
  const [trainingFile, setTrainingFile] = useState(null);

  const knowledgeBaseLimitReached = () => sizeInBytes(knowledgeBaseSize) > sizeInBytes(knowledgeBaseLimit);

  // ── Load ──────────────────────────────────────────────────────────────────
  const fetchConfig = async () => {
    const res = await aiFetch(getValidToken, AI_BOT_CONFIG_ENDPOINT);
    if (!res.ok || res.data?.success === false) {
      notify("error", "Could not load the configuration.");
      return;
    }
    const data = res.data.data;

    setBotType(data.bot_type);
    setAiOverride(data.ai_override || null);
    setRetrievedDocumentCount(data.retrieved_document_count);
    setRateLimit(data.rate_limit);
    setAnswerLimit(data.answer_character_limit);
    setMaxHistoryMessages(data.max_history_messages);
    setRewritePrompt(data.rewrite_prompt || "");
    setAnswerPrompt(data.answer_prompt || "");
    setRelevanceThreshold(data.relevance_threshold ?? "");
    setRelevanceDefault(data.embedding_relevance_default || null);
    setNotFoundReply(data.not_found_reply || "");
    setKnowledgeBaseTopics(data.knowledge_base_topics || "");
    setHistoryAnswerChars(data.history_answer_chars);
    setMaxAnswerTokens(data.max_answer_tokens);
    setAnswerThinking(data.answer_thinking || "none");
    setBroadQuestionSearch(!!data.broad_question_search);
    setCondenseLongAnswers(!!data.condense_long_answers);
    setConciseAnswers(!!data.concise_answers);
    setSkipFirstRewrite(!!data.skip_first_rewrite);
    setSmallTalkEnabled(!!data.small_talk_enabled);
    setGreetingReply(data.greeting_reply || "");
    setThanksReply(data.thanks_reply || "");
    setClosingReply(data.closing_reply || "");

    setKnowledgeBaseSize(data.knowledge_base_size);
    setKnowledgeBaseLimit(data.knowledge_base_limit);
    setKnowledgeBaseStale(!!data.knowledge_base_stale);
    setKnowledgeBaseChunks(data.knowledge_base_chunks || 0);
    setBaseDomain(data.base_domain || "");
    setChunkSize(data.chunk_size);
    setChunkOverlap(data.chunk_overlap);
    setScrapeStatus(data.scrape_status);
    setTimeSinceLastScrape(data.time_since_last_scrape);
    setScrapeAlert({ type: "error", message: data.scrape_status === "failed" ? data.scrape_error : "" });
    setDocumentTrainingStatus(data.document_training_status);
    setTimeSinceLastDocumentTraining(data.time_since_last_document_training);
    setDocumentTrainingAlert({
      type: "error",
      message: data.document_training_status === "failed" ? data.document_training_error : "",
    });
    setHealthRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchConfig();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchConfig = (body) => aiFetch(getValidToken, AI_BOT_CONFIG_ENDPOINT, { method: "PATCH", body });

  // ── AI Bot ────────────────────────────────────────────────────────────────
  const validateBot = () => {
    const e = {};
    if (retrievedDocumentCount < 1 || retrievedDocumentCount > 20) e.retrievedDocumentCount = "Must be between 1 and 20";
    if (rateLimit < 0 || rateLimit > 20) e.rateLimit = "Must be between 0 and 20 seconds";
    if (answerLimit < 100 || answerLimit > 10000) e.answerLimit = "Must be between 100 and 10,000 characters";
    if (rewritePrompt.length > 60000) e.rewritePrompt = "Cannot exceed 60,000 characters";
    if (answerPrompt.length > 60000) e.answerPrompt = "Cannot exceed 60,000 characters";
    if (relevanceThreshold !== "" && (Number(relevanceThreshold) < 0 || Number(relevanceThreshold) > 1 || isNaN(Number(relevanceThreshold))))
      e.relevanceThreshold = "Must be between 0 and 1, or blank for the default";
    if (!notFoundReply.trim()) e.notFoundReply = "Enter the reply sent when nothing relevant is found";
    if (knowledgeBaseTopics.length > 2000) e.knowledgeBaseTopics = "Cannot exceed 2,000 characters";
    if (maxHistoryMessages < 0 || maxHistoryMessages > 20) e.maxHistoryMessages = "Must be between 0 and 20 messages";
    if (historyAnswerChars < 50 || historyAnswerChars > 4000) e.historyAnswerChars = "Must be between 50 and 4,000 characters";
    if (maxAnswerTokens < 50 || maxAnswerTokens > 4000) e.maxAnswerTokens = "Must be between 50 and 4,000 tokens";
    if (smallTalkEnabled) {
      if (!greetingReply.trim()) e.greetingReply = "Enter a greeting reply";
      if (!thanksReply.trim()) e.thanksReply = "Enter a thanks reply";
      if (!closingReply.trim()) e.closingReply = "Enter a closing reply";
    }
    return e;
  };

  const saveBot = async () => {
    const errors = validateBot();
    setBotErrors(errors);
    if (Object.keys(errors).length) {
      notify("error", "Please fix the highlighted fields.");
      return;
    }
    setSavingBot(true);
    const res = await patchConfig({
      ai_override: aiOverride,
      retrieved_document_count: Number(retrievedDocumentCount),
      rate_limit: Number(rateLimit),
      answer_character_limit: Number(answerLimit),
      rewrite_prompt: rewritePrompt,
      answer_prompt: answerPrompt,
      max_history_messages: Number(maxHistoryMessages),
      relevance_threshold: relevanceThreshold === "" ? null : Number(relevanceThreshold),
      not_found_reply: notFoundReply,
      knowledge_base_topics: knowledgeBaseTopics,
      history_answer_chars: Number(historyAnswerChars),
      max_answer_tokens: Number(maxAnswerTokens),
      answer_thinking: answerThinking,
      broad_question_search: broadQuestionSearch,
      condense_long_answers: condenseLongAnswers,
      concise_answers: conciseAnswers,
      skip_first_rewrite: skipFirstRewrite,
      small_talk_enabled: smallTalkEnabled,
      greeting_reply: greetingReply,
      thanks_reply: thanksReply,
      closing_reply: closingReply,
    });
    setSavingBot(false);
    if (!res.ok || res.data?.success === false) {
      notify("error", errorMessage(res.data, "Failed to save the AI Bot settings."));
      return;
    }
    notify("success", "AI Bot settings saved.");
    setHealthRefreshKey((k) => k + 1);
  };

  // ── Knowledge base ────────────────────────────────────────────────────────
  // Chunking and the website apply to the next training run, so they are saved when
  // training starts rather than with a separate button.
  const saveKnowledgeBaseSettings = async ({ requireDomain }) => {
    const e = {};
    if (requireDomain && (!baseDomain || !DOMAIN_REGEX.test(baseDomain)))
      e.baseDomain = "Enter a valid website URL (e.g. https://example.com)";
    if (chunkSize < 100 || chunkSize > 10000) e.chunkSize = "Must be between 100 and 10,000 words";
    if (chunkOverlap < 0 || chunkOverlap > 1000) e.chunkOverlap = "Must be between 0 and 1,000 words";
    else if (Number(chunkOverlap) >= Number(chunkSize)) e.chunkOverlap = "Must be less than the chunk size";
    setKbErrors(e);
    if (Object.keys(e).length) return false;

    const res = await patchConfig({
      base_domain: baseDomain || null,
      chunk_size: Number(chunkSize),
      chunk_overlap: Number(chunkOverlap),
    });
    if (!res.ok) {
      notify("error", errorMessage(res.data, "Failed to save the knowledge base settings."));
      return false;
    }
    return true;
  };

  const startJob = async (endpoint, setAlert, startedMessage, failedMessage, options = {}) => {
    setAlert({ type: "", message: "" });
    const res = options.formData
      ? await fetch(endpoint, {
          method: "POST",
          headers: { Authorization: `Bearer ${await getValidToken()}`, "X-Tenant-Id": localStorage.getItem("tenant") },
          body: options.formData,
        }).then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => null) }))
      : await aiFetch(getValidToken, endpoint, { method: "POST" });
    const ok = res.ok && res.data?.success !== false;
    setAlert({ type: ok ? "success" : "error", message: ok ? startedMessage : errorMessage(res.data, failedMessage) });
    await fetchConfig();
    return ok;
  };

  const trainWebsite = async () => {
    if (!(await saveKnowledgeBaseSettings({ requireDomain: true }))) return;
    await startJob(AI_SCRAPE_WEBSITE_ENDPOINT, setScrapeAlert, "Website training started.", "Failed to start website training.");
  };

  const stopWebsite = () =>
    startJob(AI_STOP_SCRAPE_WEBSITE_ENDPOINT, setScrapeAlert, "Website training stopped.", "Failed to stop website training.");

  const trainDocument = async () => {
    if (!trainingFile) {
      setDocumentTrainingAlert({ type: "error", message: "Choose a PDF file to upload." });
      return;
    }
    if (trainingFile.type !== "application/pdf") {
      setDocumentTrainingAlert({ type: "error", message: "Only PDF files are supported." });
      return;
    }
    if (trainingFile.size > 20 * 1024 * 1024) {
      setDocumentTrainingAlert({ type: "error", message: "The PDF must be smaller than 20 MB." });
      return;
    }
    if (!(await saveKnowledgeBaseSettings({ requireDomain: false }))) return;
    const formData = new FormData();
    formData.append("file", trainingFile);
    const ok = await startJob(
      AI_TRAIN_DOCUMENT_ENDPOINT,
      setDocumentTrainingAlert,
      "Document uploaded. Training started.",
      "Failed to upload the document for training.",
      { formData },
    );
    if (ok) setTrainingFile(null);
  };

  const stopDocument = () =>
    startJob(
      AI_STOP_TRAIN_DOCUMENT_ENDPOINT,
      setDocumentTrainingAlert,
      "Document training stopped.",
      "Failed to stop document training.",
    );

  const reembed = () =>
    startJob(
      AI_REEMBED_KNOWLEDGE_BASE_ENDPOINT,
      setDocumentTrainingAlert,
      "Re-embedding started. Refresh the status in a moment.",
      "Could not start re-embedding.",
    );

  // Deletes every website and document passage; the bot then replies "not found" until retrained.
  const clearKnowledgeBase = async () => {
    setClearing(true);
    const res = await aiFetch(getValidToken, AI_CLEAR_KNOWLEDGE_BASE_ENDPOINT, { method: "POST" });
    setClearing(false);
    setClearDialogOpen(false);
    if (!res.ok || res.data?.success === false) {
      notify("error", errorMessage(res.data, "Could not clear the knowledge base."));
      return;
    }
    notify("success", `Knowledge base cleared (${res.data.deleted_chunks} passages deleted).`);
    await fetchConfig();
  };

  // Drafts the topics list from the knowledge base (one AI call); saved with the other settings.
  const generateTopics = async () => {
    setGeneratingTopics(true);
    const res = await aiFetch(getValidToken, AI_KNOWLEDGE_BASE_TOPICS_ENDPOINT, { method: "POST" });
    setGeneratingTopics(false);
    if (!res.ok || res.data?.success === false) {
      notify("error", errorMessage(res.data, "Could not generate the topics."));
      return;
    }
    setKnowledgeBaseTopics(res.data.topics || "");
    notify("success", "Topics drafted. Review them, then save the AI Bot settings.");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const limitReached = knowledgeBaseLimitReached();
  const isWebsiteBot = botType === "scraped_website";

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", boxSizing: "border-box" }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Choose the AI models your bots use, tune how the AI Bot answers, and train it on your content.
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You can view this configuration but not change it.
        </Alert>
      )}

      <CustomTabs
        value={tab}
        onChange={(e, next) => setTab(next)}
        tabs={[
          { label: "AI models", value: "models" },
          { label: "AI Bot", value: "bot" },
          { label: knowledgeBaseStale ? "Knowledge base • needs re-embedding" : "Knowledge base", value: "kb" },
        ]}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* 1. AI models (tenant-wide) */}
        {tab === "models" && <AISettingsPanel disabled={!canEdit} onSaved={fetchConfig} />}

        {/* 2. AI Bot */}
        {tab === "bot" && (
          <SectionCard
            title="AI Bot"
            description="How the AI Bot answers your customers on WhatsApp."
            footer={
              <ButtonOne
                title={savingBot ? "Saving..." : "Save AI Bot settings"}
                onClick={saveBot}
                startIcon={savingBot ? <CircularProgress size={16} /> : <Save />}
                disabled={savingBot || !canEdit}
              />
            }
          >
            <AIConnectionHealthBanner override={aiOverride} refreshKey={healthRefreshKey} />

            <FieldGroup
              title="Chat model"
              description="Uses the AI models default unless you pick a model just for the AI Bot."
            >
              <Box sx={{ maxWidth: 720 }} data-testid="ai-bot-model">
                <AIModelPicker
                  label="Chat model for the AI Bot"
                defaultLabel="Use the default from the AI models tab"
                  capability="chat"
                  allowTenantDefault
                  value={aiOverride}
                  onChange={setAiOverride}
                  disabled={!canEdit}
                />
              </Box>
            </FieldGroup>

            {isWebsiteBot && (
              <FieldGroup
                title="Knowledge base search"
                description="Which passages from your website and documents are sent with each question."
              >
                <Box sx={twoColumns}>
                  <TextField
                    label="Documents retrieved per question"
                    type="number"
                    size="small"
                    value={retrievedDocumentCount}
                    onChange={(e) => setRetrievedDocumentCount(e.target.value)}
                    disabled={!canEdit}
                    error={!!botErrors.retrievedDocumentCount}
                    helperText={
                      botErrors.retrievedDocumentCount ||
                      "Up to this many knowledge-base passages are sent with each question. Fewer costs less."
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">Documents</InputAdornment> }}
                  />
                  <TextField
                    label="Relevance threshold"
                    type="number"
                    size="small"
                    value={relevanceThreshold}
                    onChange={(e) => setRelevanceThreshold(e.target.value)}
                    disabled={!canEdit}
                    placeholder={relevanceDefault ? `Default: ${relevanceDefault.threshold}` : "Default"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 0.01, min: 0, max: 1, "data-testid": "relevance-threshold" }}
                    error={!!botErrors.relevanceThreshold}
                    helperText={
                      botErrors.relevanceThreshold ||
                      `Passages scoring below this (0 to 1) are left out; if none remain, the bot sends the reply below and skips the AI answer. Leave blank for the default for ${relevanceDefault?.display_name || "your embedding model"}${relevanceDefault ? ` (${relevanceDefault.threshold})` : ""}.`
                    }
                  />
                </Box>
                <TextField
                  label="Reply when nothing relevant is found"
                  multiline
                  minRows={2}
                  fullWidth
                  value={notFoundReply}
                  onChange={(e) => setNotFoundReply(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.notFoundReply}
                  helperText={
                    botErrors.notFoundReply ||
                    "Sent when the knowledge base has no answer (no extra AI call). {topics} is replaced by the topics below."
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  label="Topics the bot can help with"
                  multiline
                  minRows={3}
                  fullWidth
                  value={knowledgeBaseTopics}
                  onChange={(e) => setKnowledgeBaseTopics(e.target.value)}
                  disabled={!canEdit || generatingTopics}
                  error={!!botErrors.knowledgeBaseTopics}
                  helperText={
                    botErrors.knowledgeBaseTopics ||
                    "A short list shown to customers in the reply above. Generate a draft from the knowledge base, then edit it."
                  }
                  inputProps={{ "data-testid": "knowledge-base-topics" }}
                  sx={{ mt: 2 }}
                />
                {canEdit && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={generateTopics}
                      disabled={generatingTopics || !knowledgeBaseChunks}
                      startIcon={generatingTopics ? <CircularProgress size={14} /> : <AutoAwesome />}
                      sx={{ textTransform: "none" }}
                    >
                      Generate from knowledge base
                    </Button>
                  </Box>
                )}
              </FieldGroup>
            )}

            <FieldGroup title="Answer style and length" description="How the AI writes its answers, and how long they can be.">
              <SwitchRow
                label="Concise answers"
                description="Ask for WhatsApp-length answers: the direct answer first, then a few short points. Customers can ask for more detail."
                checked={conciseAnswers}
                onChange={setConciseAnswers}
                disabled={!canEdit}
              />
              <SwitchRow
                label="Search broad questions by topic"
                description="For questions like “what compliances apply to…”, search 3–5 sub-topics separately so every part gets passages. One small extra AI step per question."
                checked={broadQuestionSearch}
                onChange={setBroadQuestionSearch}
                disabled={!canEdit}
              />
              <SwitchRow
                label="Condense long answers"
                description="Let the AI draft long answers with twice the room, then shorten them to the answer length limit keeping every fact. Adds an AI call only when an answer runs over."
                checked={condenseLongAnswers}
                onChange={setCondenseLongAnswers}
                disabled={!canEdit}
              />
              <Box sx={{ ...twoColumns, mt: 2 }}>
                <TextField
                  label="Maximum answer length"
                  type="number"
                  size="small"
                  value={maxAnswerTokens}
                  onChange={(e) => setMaxAnswerTokens(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.maxAnswerTokens}
                  helperText={
                    botErrors.maxAnswerTokens ||
                    "Hard stop on what the AI writes (about 3.5 characters per token). A cut-off answer ends at its last complete line. Answer tokens cost 4x question tokens."
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">Tokens</InputAdornment> }}
                />
                <TextField
                  label="Answer length limit"
                  type="number"
                  size="small"
                  value={answerLimit}
                  onChange={(e) => setAnswerLimit(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.answerLimit}
                  helperText={
                    botErrors.answerLimit ||
                    "The length the AI aims for (never more than the maximum answer length allows); anything longer is trimmed at a sentence end. Over 4,095 characters is sent as several WhatsApp messages."
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">Characters</InputAdornment> }}
                />
                <TextField
                  select
                  label="Thinking before answering"
                  size="small"
                  value={answerThinking}
                  onChange={(e) => setAnswerThinking(e.target.value)}
                  disabled={!canEdit}
                  helperText="Lets the AI check all the notes before writing, for fuller answers to multi-part questions. Thinking is billed as output tokens, on top of the maximum answer length: up to 1,024 (Low) or 4,096 (Medium) on Gemini, 4,096 or 8,192 on Claude. Gemini 2.5+ and Claude 5 models only."
                  SelectProps={{ native: true }}
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                </TextField>
              </Box>
            </FieldGroup>

            <FieldGroup
              title="Conversation"
              description="Earlier messages sent along so the bot understands follow-up questions, and how often customers can ask."
            >
              <Box sx={twoColumns}>
                <TextField
                  label="Conversation history"
                  type="number"
                  size="small"
                  value={maxHistoryMessages}
                  onChange={(e) => setMaxHistoryMessages(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.maxHistoryMessages}
                  helperText={botErrors.maxHistoryMessages || "Previous messages kept as context for each question (0 turns it off)."}
                  InputProps={{ endAdornment: <InputAdornment position="end">Messages</InputAdornment> }}
                />
                <TextField
                  label="Characters kept from earlier answers"
                  type="number"
                  size="small"
                  value={historyAnswerChars}
                  onChange={(e) => setHistoryAnswerChars(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.historyAnswerChars}
                  helperText={
                    botErrors.historyAnswerChars ||
                    "Earlier bot answers are shortened to this when sent as history: enough to follow the topic, much cheaper than whole answers."
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">Characters</InputAdornment> }}
                />
                <TextField
                  label="Minimum time between questions"
                  type="number"
                  size="small"
                  value={rateLimit}
                  onChange={(e) => setRateLimit(e.target.value)}
                  disabled={!canEdit}
                  error={!!botErrors.rateLimit}
                  helperText={botErrors.rateLimit || "Slows down customers who send many messages in a row."}
                  InputProps={{ endAdornment: <InputAdornment position="end">Seconds</InputAdornment> }}
                />
              </Box>
            </FieldGroup>

            <FieldGroup title="Cost savers" description="Skip AI calls that don't change the answer.">
              <SwitchRow
                label="Skip the question rewrite on first questions"
                description="A first question has nothing earlier to refer to, so it's searched as typed, saving one AI call. Questions that mention time (upcoming, latest...) and bots with rewrite instructions are still rewritten."
                checked={skipFirstRewrite}
                onChange={setSkipFirstRewrite}
                disabled={!canEdit}
              />
              <SwitchRow
                label="Reply to greetings and thanks without AI"
                description="Messages like hi, thanks or bye get the replies below, with no AI call."
                checked={smallTalkEnabled}
                onChange={setSmallTalkEnabled}
                disabled={!canEdit}
              />
              {smallTalkEnabled && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mt: 2 }}>
                  {[
                    ["Greeting reply", greetingReply, setGreetingReply, "greetingReply"],
                    ["Thanks reply", thanksReply, setThanksReply, "thanksReply"],
                    ["Goodbye reply", closingReply, setClosingReply, "closingReply"],
                  ].map(([label, value, setValue, key]) => (
                    <TextField
                      key={key}
                      label={label}
                      multiline
                      minRows={2}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      disabled={!canEdit}
                      error={!!botErrors[key]}
                      helperText={botErrors[key] || " "}
                    />
                  ))}
                </Box>
              )}
            </FieldGroup>

            <FieldGroup title="Instructions" description="Extra guidance added to the AI Bot's prompts.">
              <Box sx={twoColumns}>
                <TextField
                  label="Question rewrite instructions"
                  multiline
                  minRows={8}
                  value={rewritePrompt}
                  onChange={(e) => setRewritePrompt(e.target.value)}
                  disabled={!canEdit}
                  InputProps={{ sx: { fontSize: 14 } }}
                  error={!!botErrors.rewritePrompt}
                  helperText={botErrors.rewritePrompt || "How a customer's question is rewritten before searching."}
                />
                <TextField
                  label="Answer instructions"
                  multiline
                  minRows={8}
                  value={answerPrompt}
                  onChange={(e) => setAnswerPrompt(e.target.value)}
                  disabled={!canEdit}
                  InputProps={{ sx: { fontSize: 14 } }}
                  error={!!botErrors.answerPrompt}
                  helperText={botErrors.answerPrompt || "How the final answer is written from what was found."}
                />
              </Box>
            </FieldGroup>
          </SectionCard>
        )}

        {/* 3. Knowledge base */}
        {tab === "kb" && (
          <SectionCard
            title="Knowledge base"
            description="Website pages and documents the AI Bot searches to answer questions."
            headerAction={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={limitReached ? "error" : "default"}
                  label={`${knowledgeBaseSize || "0 kB"} of ${knowledgeBaseLimit || "—"}`}
                />
                <Tooltip title="Refresh training status">
                  <IconButton size="small" onClick={fetchConfig} aria-label="Refresh training status">
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteForever />}
                    onClick={() => setClearDialogOpen(true)}
                    disabled={
                      !knowledgeBaseChunks || documentTrainingStatus === "running" || scrapeStatus === "running"
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Clear knowledge base
                  </Button>
                )}
                <Dialog open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)} maxWidth="xs">
                  <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmber color="error" /> Clear the knowledge base?
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This permanently deletes all {knowledgeBaseChunks.toLocaleString("en-IN")} passages (
                      {knowledgeBaseSize || "0 kB"}) from your website and documents. It can&apos;t be undone.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 1.5 }}>
                      Until you train again, the AI Bot will reply that it couldn&apos;t find an answer.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setClearDialogOpen(false)} disabled={clearing} sx={{ textTransform: "none" }}>
                      Cancel
                    </Button>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={clearKnowledgeBase}
                      disabled={clearing}
                      startIcon={clearing ? <CircularProgress size={16} /> : <DeleteForever />}
                      sx={{ textTransform: "none" }}
                    >
                      Clear knowledge base
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            }
          >
            {knowledgeBaseStale && (
              <Alert
                severity="warning"
                action={
                  canEdit && (
                    <ButtonOne
                      title="Re-embed knowledge base"
                      onClick={reembed}
                      startIcon={<Refresh />}
                      disabled={documentTrainingStatus === "running"}
                    />
                  )
                }
              >
                Knowledge base out of date — it was built with a different embedding model, so the bot can&apos;t search it
                until it is re-embedded with the current one.
              </Alert>
            )}
            {limitReached && (
              <Alert severity="error">The knowledge base has reached its size limit, so new training is paused.</Alert>
            )}

            <FieldGroup title="Chunking" description="How content is split before it is embedded. Applies to the next training run. New bots start at 300 words with 50 overlap: smaller passages keep each answer's input, and cost, down.">
              <Box sx={twoColumns}>
                <TextField
                  label="Chunk size"
                  type="number"
                  size="small"
                  value={chunkSize}
                  onChange={(e) => setChunkSize(e.target.value)}
                  disabled={!canEdit}
                  error={!!kbErrors.chunkSize}
                  helperText={kbErrors.chunkSize || "Larger chunks keep more context but match less precisely."}
                  InputProps={{ endAdornment: <InputAdornment position="end">Words</InputAdornment> }}
                />
                <TextField
                  label="Chunk overlap"
                  type="number"
                  size="small"
                  value={chunkOverlap}
                  onChange={(e) => setChunkOverlap(e.target.value)}
                  disabled={!canEdit}
                  error={!!kbErrors.chunkOverlap}
                  helperText={kbErrors.chunkOverlap || "Words shared between neighbouring chunks."}
                  InputProps={{ endAdornment: <InputAdornment position="end">Words</InputAdornment> }}
                />
              </Box>
            </FieldGroup>

            <TrainingRow
              title="Website"
              description="Indexes the pages of your website, starting from the address below."
              status={scrapeStatus}
              lastTrained={timeSinceLastScrape}
              alert={scrapeAlert}
              action={
                scrapeStatus === "running" ? (
                  <ButtonOne title="Stop" variant="outlined" onClick={stopWebsite} startIcon={<Stop />} disabled={!canEdit} />
                ) : (
                  <ButtonOne
                    title="Train from website"
                    onClick={trainWebsite}
                    startIcon={<BrowserUpdated />}
                    disabled={!canEdit || limitReached}
                  />
                )
              }
            >
              <TextField
                fullWidth
                size="small"
                label="Website address"
                placeholder="https://example.com"
                value={baseDomain}
                onChange={(e) => setBaseDomain(e.target.value)}
                disabled={!canEdit || scrapeStatus === "running"}
                error={!!kbErrors.baseDomain}
                helperText={kbErrors.baseDomain}
              />
            </TrainingRow>

            <TrainingRow
              title="Documents"
              description="Upload a PDF (up to 20 MB) to add internal knowledge."
              status={documentTrainingStatus}
              lastTrained={timeSinceLastDocumentTraining}
              alert={documentTrainingAlert}
              action={
                documentTrainingStatus === "running" ? (
                  <ButtonOne title="Stop" variant="outlined" onClick={stopDocument} startIcon={<Stop />} disabled={!canEdit} />
                ) : (
                  <ButtonOne
                    title="Train from document"
                    onClick={trainDocument}
                    startIcon={<UploadFile />}
                    disabled={!canEdit || limitReached}
                  />
                )
              }
            >
              <TextField
                type="file"
                size="small"
                inputProps={{ accept: "application/pdf" }}
                disabled={!canEdit}
                onChange={(e) => {
                  setTrainingFile(e.target.files?.[0] || null);
                  setDocumentTrainingAlert({ type: "", message: "" });
                }}
                helperText={trainingFile ? `Selected: ${trainingFile.name}` : "PDF only"}
                sx={{ maxWidth: 480 }}
              />
            </TrainingRow>
          </SectionCard>
        )}
      </Box>

      <Snackbar
        open={!!snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ type: "", message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ type: "", message: "" })}
          severity={snackbar.type || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
